/**
 * Brazilian CNPJ: 14 characters, the first 12 letters or digits (alphanumeric CNPJ issued since July 2026)
 * and two numeric check digits. Same algorithm as FillSign.Infrastructure/Helpers/CnpjValidator.cs:
 * each character counts as its char code minus 48 (A = 17 … Z = 42), weights 2–9 from right to left.
 */

/** Uppercase letters and digits only. */
export const cleanCnpj = (value: string | null | undefined): string =>
  (value || "").toUpperCase().replace(/[^0-9A-Z]/g, "");

/** Mask while typing: "12.ABC.345/01DE-35". Letters only in the first 12 positions. */
export function maskCnpj(value: string | null | undefined): string {
  const raw = cleanCnpj(value);
  let c = "";
  for (const ch of raw) {
    if (c.length >= 14) break;
    if (c.length >= 12 && !/\d/.test(ch)) continue;   // check digits are numeric
    c += ch;
  }
  const parts = [c.slice(0, 2), c.slice(2, 5), c.slice(5, 8), c.slice(8, 12), c.slice(12, 14)];
  let out = parts[0];
  if (parts[1]) out += `.${parts[1]}`;
  if (parts[2]) out += `.${parts[2]}`;
  if (parts[3]) out += `/${parts[3]}`;
  if (parts[4]) out += `-${parts[4]}`;
  return out;
}

const value = (ch: string) => ch.charCodeAt(0) - 48;

function checkDigit(c: string, weights: number[]): number {
  let sum = 0;
  for (let i = 0; i < weights.length; i++) sum += value(c[i]) * weights[i];
  const rest = sum % 11;
  return rest < 2 ? 0 : 11 - rest;
}

export function isCnpjValid(input: string | null | undefined): boolean {
  const c = cleanCnpj(input);
  if (!/^[0-9A-Z]{12}\d{2}$/.test(c)) return false;
  if (/^(.)\1+$/.test(c)) return false;
  return Number(c[12]) === checkDigit(c, [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2])
    && Number(c[13]) === checkDigit(c, [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
}
